/**
 * Command Executor for AI Presentation Layer Editing
 *
 * Executes MMO commands by directly modifying cloned MMOs and using COMMIT
 * to update the flow context. This bypasses the DELEGATE → child machine →
 * commitToParent chain which can fail silently when parentSend is undefined.
 *
 * For screen-level operations (background color), deselects objects first
 * since SET_COLOR requires !ctx.currentObject.
 */

import {
  MMOCommand,
  MMOEditCommand,
  ScreenEditCommand,
  CreateCommand,
  DeleteCommand,
  ReorderCommand,
  ScreenAddCommand,
  ScreenDeleteCommand,
  ScreenDuplicateCommand,
  CommandResult,
  CommandOperation,
  commandColorToColor,
  validateCommand,
} from './CommandSchema';
import { resolveTarget, ResolutionContext } from './TargetResolver';
import { parseColor } from './ColorMap';
import { parseFont, applyFontStyle, FONT_STYLE_SUFFIXES } from './FontMap';
import { FlowObjects, FlowMachineContext, ADD_BACKGROUND_MMO, ReorderKind } from '../../fsm/types';
import TextFlowObject from '../../../model/TextFlowObject';
import ShapeFlowObject, { ShapeType } from '../../../model/ShapeFlowObject';
import Color from '../../../model/Color';
import { Alignment } from '../../../model/FontAttrsArray';
import Screens, { OrderShift } from '../../../model/Screens';
import { guidGenerator, getCurrentDate } from '../../utils/commonFunctions';
import FontAttrsArray from '../../../model/FontAttrsArray';
import GalleryAsset from '../../../model/GalleryAsset';
import { GradientDef } from '../../../ai/styles/themeDecorations';

/**
 * FSM event sender type
 */
type EventSender = (event: any) => void;

/**
 * Execution context for commands
 */
export interface ExecutionContext {
  /** Current FSM context */
  machineContext: FlowMachineContext;
  /** Current screen index */
  screenIndex: number;
  /** Content mapping from flow */
  contentMapping?: Record<string, { screenIndex: number; mmoIds: string[] }>;
}

/**
 * Deep clone an MMO for modification.
 * Cannot use JSON.stringify because FontAttrsArray.toJSON() and Color.toJSON()
 * return strings, corrupting the data. Instead, we recursively clone while
 * preserving class prototypes and skipping __ref (restored by COMMIT handler).
 */
function deepCloneValue(val: any): any {
  if (val === null || val === undefined) return val;
  if (typeof val !== 'object') return val;

  // Arrays: clone each element recursively
  if (Array.isArray(val)) {
    return val.map((item: any) => deepCloneValue(item));
  }

  // Objects: preserve prototype chain (Color, FontAttrsArray, etc.)
  const proto = Object.getPrototypeOf(val);
  const clone = Object.create(proto);
  for (const key of Object.keys(val)) {
    if (key === '__ref') continue; // Skip XState interpreter reference
    clone[key] = deepCloneValue(val[key]);
  }
  return clone;
}

function cloneMmo(mmo: FlowObjects): any {
  return deepCloneValue(mmo);
}

/**
 * Command Executor class
 */
export class CommandExecutor {
  private send: EventSender;

  constructor(send: EventSender) {
    this.send = send;
  }

  /**
   * Execute a single command
   */
  execute(command: MMOCommand, context: ExecutionContext): CommandResult {
    // Validate command structure
    const validation = validateCommand(command);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    try {
      switch (command.type) {
        case 'mmo_edit':
          return this.executeMMOEdit(command, context);
        case 'screen_edit':
          return this.executeScreenEdit(command, context);
        case 'create':
        case 'mmo_add' as any:
          return this.executeCreate(command as any, context);
        case 'delete':
          return this.executeDelete(command, context);
        case 'reorder':
          return this.executeReorder(command, context);
        case 'screen_add':
          return this.executeScreenAdd(command as ScreenAddCommand, context);
        case 'screen_delete':
          return this.executeScreenDelete(command as ScreenDeleteCommand, context);
        case 'screen_duplicate':
          return this.executeScreenDuplicate(command as ScreenDuplicateCommand, context);
        default:
          return { success: false, error: `Unknown command type: ${(command as any).type}` };
      }
    } catch (error) {
      console.error('[AI CommandExecutor] Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error during execution',
      };
    }
  }

  /**
   * Execute multiple commands in sequence
   */
  executeBatch(commands: MMOCommand[], context: ExecutionContext): CommandResult[] {
    return commands.map((cmd) => this.execute(cmd, context));
  }

  /**
   * Execute a command for multiple targets (multi-element operations).
   * Clones the command for each target index and executes sequentially.
   */
  executeForTargets(
    command: MMOCommand,
    targetIndices: number[],
    context: ExecutionContext
  ): CommandResult[] {
    return targetIndices.map((mmoIndex) => {
      const cloned: MMOCommand = JSON.parse(JSON.stringify(command));
      cloned.target = { ...cloned.target, mmoIndex };
      return this.execute(cloned, context);
    });
  }

  /**
   * Execute MMO edit command.
   * Clones the MMO, applies all operations, then COMMITs once.
   */
  private executeMMOEdit(command: MMOEditCommand, context: ExecutionContext): CommandResult {
    const screen = context.machineContext.flow.screens[context.screenIndex];
    if (!screen) {
      return { success: false, error: `Screen ${context.screenIndex} not found` };
    }

    // Resolve target
    const resolutionContext: ResolutionContext = {
      screen,
      screenIndex: context.screenIndex,
      contentMapping: context.contentMapping,
    };

    const resolved = resolveTarget(command.target, resolutionContext);
    if (!resolved.mmo) {
      return { success: false, error: resolved.reason };
    }

    const mmo = resolved.mmo;
    const mmoId = mmo.id;

    // Clone the MMO to bypass Immer freeze and avoid DELEGATE chain
    const clone = cloneMmo(mmo);
    console.log(`[AI Executor] Editing MMO ${mmoId} (type: ${mmo.type}, index: ${resolved.index})`);

    // Apply each operation to the clone
    for (const operation of command.operations) {
      const result = this.applyOperationToClone(clone, mmo, operation);
      if (!result.success) {
        return result;
      }
    }

    // COMMIT the modified clone directly to the flow context
    // The COMMIT handler will restore __ref from the existing MMO
    this.send({
      type: 'COMMIT',
      mmo: clone,
      id: mmoId,
      shouldDraft: false,
      screenIndex: context.screenIndex,
    });

    console.log(`[AI Executor] COMMITted changes for MMO ${mmoId}`);
    return { success: true, mmoId, screenIndex: context.screenIndex };
  }

  /**
   * Apply a single operation to a cloned MMO.
   * Modifies the clone in-place. Uses the original (frozen) MMO for reading current values.
   */
  private applyOperationToClone(
    clone: any,
    original: FlowObjects,
    operation: CommandOperation
  ): CommandResult {
    const { property, value, relative } = operation;
    const mmoId = original.id;

    switch (property) {
      // Position operations
      case 'frame.x':
      case 'x': {
        const newX = relative ? original.frame.x + value : value;
        clone.frame = { ...clone.frame, x: newX };
        return { success: true, mmoId };
      }

      case 'frame.y':
      case 'y': {
        const newY = relative ? original.frame.y + value : value;
        clone.frame = { ...clone.frame, y: newY };
        return { success: true, mmoId };
      }

      case 'position': {
        const newX = relative ? original.frame.x + (value.x || 0) : (value.x ?? original.frame.x);
        const newY = relative ? original.frame.y + (value.y || 0) : (value.y ?? original.frame.y);
        clone.frame = { ...clone.frame, x: newX, y: newY };
        return { success: true, mmoId };
      }

      // Size operations
      case 'frame.width':
      case 'width': {
        const newWidth = relative ? original.frame.width + value : value;
        clone.frame = { ...clone.frame, width: Math.max(10, newWidth) };
        return { success: true, mmoId };
      }

      case 'frame.height':
      case 'height': {
        const newHeight = relative ? original.frame.height + value : value;
        clone.frame = { ...clone.frame, height: Math.max(10, newHeight) };
        return { success: true, mmoId };
      }

      case 'size': {
        const newWidth = relative ? original.frame.width + (value.width || 0) : (value.width ?? original.frame.width);
        const newHeight = relative ? original.frame.height + (value.height || 0) : (value.height ?? original.frame.height);
        clone.frame = { ...clone.frame, width: Math.max(10, newWidth), height: Math.max(10, newHeight) };
        return { success: true, mmoId };
      }

      case 'frame': {
        clone.frame = {
          x: value.x ?? original.frame.x,
          y: value.y ?? original.frame.y,
          width: Math.max(10, value.width ?? original.frame.width),
          height: Math.max(10, value.height ?? original.frame.height),
        };
        return { success: true, mmoId };
      }

      // Text font size
      case 'font.fontSize':
      case 'fontSize': {
        if (original.type !== 'text') {
          return { success: false, error: 'Font size can only be set on text elements' };
        }
        const currentSize = (original as any).font?.fontSize || 18;
        const newSize = Math.max(8, Math.min(200, relative ? currentSize + value : value));
        console.log(`[AI Executor] Font size: ${currentSize} → ${newSize}`);
        // Update all font size locations (mirrors TextFlowObject.changeFontSize)
        if (clone.font) clone.font.fontSize = newSize;
        if (clone.attributedString_fontArray && clone.attributedString_fontArray[0]) {
          clone.attributedString_fontArray[0].size = newSize;
        }
        if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
          clone.attributedString_flbAttributesArray[0].size = newSize;
        }
        return { success: true, mmoId };
      }

      // Text font name
      case 'font.fontName':
      case 'fontName': {
        if (original.type !== 'text') {
          return { success: false, error: 'Font name can only be set on text elements' };
        }
        // Handle style keywords like "bold", "italic", "light" by applying to current font
        const lowerValue = typeof value === 'string' ? value.toLowerCase().trim() : '';
        let fontName: string;
        if (FONT_STYLE_SUFFIXES[lowerValue] && original.font?.fontName) {
          fontName = applyFontStyle(original.font.fontName, lowerValue);
        } else {
          fontName = parseFont(value) || value;
        }
        if (clone.font) clone.font.fontName = fontName;
        if (clone.attributedString_fontArray && clone.attributedString_fontArray[0]) {
          clone.attributedString_fontArray[0].name = fontName;
        }
        if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
          clone.attributedString_flbAttributesArray[0].name = fontName;
        }
        return { success: true, mmoId };
      }

      // Text color
      case 'textColor': {
        if (original.type !== 'text') {
          return { success: false, error: 'Text color can only be set on text elements' };
        }
        const color = typeof value === 'string' ? parseColor(value) : value;
        if (!color) {
          return { success: false, error: `Invalid color: ${value}` };
        }
        const colorObj = commandColorToColor(color);
        clone.textColor = colorObj;
        if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
          clone.attributedString_flbAttributesArray[0].color = colorObj;
        }
        return { success: true, mmoId };
      }

      // Text alignment
      case 'textAlignment':
      case 'textAlign':
      case 'alignment': {
        if (original.type !== 'text') {
          return { success: false, error: 'Text alignment can only be set on text elements' };
        }
        let align: Alignment;
        switch (value.toLowerCase()) {
          case 'left':
            align = Alignment.LEFT;
            break;
          case 'center':
          case 'centre':
            align = Alignment.CENTER;
            break;
          case 'right':
            align = Alignment.RIGHT;
            break;
          default:
            return { success: false, error: `Invalid alignment: ${value}` };
        }
        clone.textAlignment = align;
        if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
          clone.attributedString_flbAttributesArray[0].alignment = align;
        }
        return { success: true, mmoId };
      }

      // Shape/text color
      case 'color':
      case 'shapeColor': {
        const color = typeof value === 'string' ? parseColor(value) : value;
        if (!color) {
          return { success: false, error: `Invalid color: ${value}` };
        }
        const colorObj = commandColorToColor(color);
        if (original.type === 'text') {
          clone.textColor = colorObj;
          if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
            clone.attributedString_flbAttributesArray[0].color = colorObj;
          }
        } else if (original.type === 'shape') {
          clone.color = colorObj;
        }
        return { success: true, mmoId };
      }

      // Border operations
      case 'borderColor': {
        const color = typeof value === 'string' ? parseColor(value) : value;
        if (!color) {
          return { success: false, error: `Invalid border color: ${value}` };
        }
        clone.borderColor = commandColorToColor(color);
        return { success: true, mmoId };
      }

      case 'borderWidth': {
        clone.borderWidth = Math.max(0, relative ? original.borderWidth + value : value);
        return { success: true, mmoId };
      }

      case 'cornerRadius': {
        clone.cornerRadius = Math.max(0, relative ? ((original as any).cornerRadius || 0) + value : value);
        return { success: true, mmoId };
      }

      // Gradient on shape
      case 'gradient':
      case 'shapeGradient': {
        if (original.type !== 'shape') {
          return { success: false, error: 'Gradient can only be set on shape elements' };
        }
        if (!value || !value.stops || value.stops.length < 2) {
          return { success: false, error: 'Gradient requires stops array with at least 2 stops' };
        }
        const gradientDef: GradientDef = {
          type: value.type || 'linear',
          angle: value.angle ?? 180,
          stops: value.stops,
        };
        clone.gradientDef = gradientDef;
        console.log(`[AI Executor] Set gradient: ${gradientDef.type} ${gradientDef.angle}deg, ${gradientDef.stops.length} stops`);
        return { success: true, mmoId };
      }

      // Opacity
      case 'opacity': {
        const opacityValue = Math.max(0, Math.min(1, relative ? ((original as any).opacity ?? 1) + value : value));
        if (original.type === 'shape') {
          // Adjust alpha channel of shape color
          const currentColor = original.color || new Color(255, 255, 255, 1);
          clone.color = new Color(currentColor.r, currentColor.g, currentColor.b, opacityValue);
        } else if (original.type === 'text') {
          // Adjust alpha channel of text color
          const currentColor = (original as any).textColor || new Color(0, 0, 0, 1);
          const newColor = new Color(currentColor.r, currentColor.g, currentColor.b, opacityValue);
          clone.textColor = newColor;
          if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
            clone.attributedString_flbAttributesArray[0].color = newColor;
          }
        }
        console.log(`[AI Executor] Set opacity: ${opacityValue}`);
        return { success: true, mmoId };
      }

      // Rotation
      case 'rotation': {
        const rotation = value as 0 | 1 | 2 | 3;
        if (![0, 1, 2, 3].includes(rotation)) {
          return { success: false, error: 'Rotation must be 0, 1, 2, or 3 (0, 90, 180, 270 degrees)' };
        }
        clone.rotation = rotation;
        return { success: true, mmoId };
      }

      // Text content
      case 'text':
      case 'content': {
        if (original.type !== 'text') {
          return { success: false, error: 'Text content can only be set on text elements' };
        }
        const newText = String(value);
        console.log(`[AI Executor] Text content: "${(original as any).text?.substring(0, 30)}..." → "${newText.substring(0, 30)}..."`);
        clone.text = newText;
        // Update font array ranges to match new text length
        if (clone.attributedString_fontArray && clone.attributedString_fontArray[0]) {
          clone.attributedString_fontArray[0].updateRange(newText.length);
        }
        if (clone.attributedString_flbAttributesArray && clone.attributedString_flbAttributesArray[0]) {
          clone.attributedString_flbAttributesArray[0].updateRange(newText.length);
        }
        return { success: true, mmoId };
      }

      // Composite font property, LLM may send {fontSize, fontName} as "font"
      case 'font': {
        if (original.type !== 'text') {
          return { success: false, error: 'Font can only be set on text elements' };
        }
        if (typeof value === 'object' && value !== null) {
          if (value.fontSize !== undefined) {
            const currentSize = (original as any).font?.fontSize || 18;
            const newSize = Math.max(8, Math.min(200, relative ? currentSize + value.fontSize : value.fontSize));
            if (clone.font) clone.font.fontSize = newSize;
            if (clone.attributedString_fontArray?.[0]) clone.attributedString_fontArray[0].size = newSize;
            if (clone.attributedString_flbAttributesArray?.[0]) clone.attributedString_flbAttributesArray[0].size = newSize;
          }
          if (value.fontName !== undefined) {
            const lv = typeof value.fontName === 'string' ? value.fontName.toLowerCase().trim() : '';
            let fn: string;
            if (FONT_STYLE_SUFFIXES[lv] && original.font?.fontName) {
              fn = applyFontStyle(original.font.fontName, lv);
            } else {
              fn = parseFont(value.fontName) || value.fontName;
            }
            if (clone.font) clone.font.fontName = fn;
            if (clone.attributedString_fontArray?.[0]) clone.attributedString_fontArray[0].name = fn;
            if (clone.attributedString_flbAttributesArray?.[0]) clone.attributedString_flbAttributesArray[0].name = fn;
          }
          return { success: true, mmoId };
        }
        return { success: false, error: 'Font property requires an object with fontSize/fontName' };
      }

      default:
        return { success: false, error: `Unknown property: ${property}` };
    }
  }

  /**
   * Execute screen edit command.
   * Deselects all objects first so SET_COLOR targets the background.
   */
  private executeScreenEdit(command: ScreenEditCommand, context: ExecutionContext): CommandResult {
    const screenIndex = command.target.screenIndex ?? context.screenIndex;

    for (const operation of command.operations) {
      switch (operation.property) {
        case 'background':
        case 'backgroundColor': {
          const color = typeof operation.value === 'string'
            ? parseColor(operation.value)
            : operation.value;
          if (!color) {
            return { success: false, error: `Invalid background color: ${operation.value}` };
          }

          // Deselect all objects first - SET_COLOR at flow level requires !ctx.currentObject
          console.log('[AI Executor] Deselecting all objects before background color change');
          this.send({ type: 'MARK_ALL_INACTIVE' });

          // Now SET_COLOR will target the screen background
          this.send({
            type: 'SET_COLOR',
            color: commandColorToColor(color),
          });
          console.log(`[AI Executor] Set background color to rgb(${color.r},${color.g},${color.b})`);
          break;
        }

        case 'backgroundGradient': {
          const gradValue = operation.value as {
            type?: 'linear' | 'radial';
            angle?: number;
            stops: { color: string; offset: number }[];
          };
          if (!gradValue || !gradValue.stops || gradValue.stops.length < 2) {
            return { success: false, error: 'backgroundGradient requires stops array with at least 2 stops' };
          }

          const gradientDef: GradientDef = {
            type: gradValue.type || 'linear',
            angle: gradValue.angle ?? 180,
            stops: gradValue.stops,
          };

          const viewSize = context.machineContext.flow.viewSize;
          const shape = new ShapeFlowObject(
            guidGenerator(), Color.iosToCssColor('00000000'), 0,
            GalleryAsset.default(), getCurrentDate(),
            { height: viewSize.height, width: viewSize.width, x: 0, y: 0 },
            null, 1, null, null, 'shape', null, null, 0,
            { height: 0, width: 0 }, null, null,
            getCurrentDate(), false,
            Color.iosToCssColor('FFFFFFFF'), ShapeType.RECTANGLE, ''
          );
          shape.gradientDef = gradientDef;

          // Deselect all objects first
          console.log('[AI Executor] Deselecting all objects before background gradient');
          this.send({ type: 'MARK_ALL_INACTIVE' });

          // Add gradient shape as background MMO (index 0, back of z-order)
          this.send({ type: ADD_BACKGROUND_MMO, mmo: shape, screenIndex });

          // Set solid background to transparent so gradient shows through
          this.send({
            type: 'SET_COLOR',
            color: new Color(0, 0, 0, 0),
          });

          console.log(`[AI Executor] Set background gradient: ${gradientDef.type} ${gradientDef.angle}deg, ${gradientDef.stops.length} stops`);
          break;
        }

        default:
          return { success: false, error: `Unknown screen property: ${operation.property}` };
      }
    }

    return { success: true, screenIndex };
  }

  /**
   * Execute create command
   */
  private executeCreate(command: CreateCommand, context: ExecutionContext): CommandResult {
    const screenIndex = command.target.screenIndex ?? context.screenIndex;
    const props = command.properties;

    let mmo: FlowObjects;
    const timestamp = getCurrentDate();
    const id = guidGenerator();

    switch (command.mmoType) {
      case 'text': {
        const fontSize = props.font?.fontSize || 24;
        const fontName = props.font?.fontName || 'HelveticaNeue';
        const text = props.text || 'New Text';

        mmo = new TextFlowObject(
          id,
          props.borderColor ? commandColorToColor(props.borderColor) : Color.default(),
          0,
          null,
          timestamp,
          {
            x: props.frame?.x ?? 100,
            y: props.frame?.y ?? 100,
            width: props.frame?.width ?? 400,
            height: props.frame?.height ?? 60,
          },
          null,
          1,
          null,
          null,
          'text',
          null,
          null,
          props.borderWidth ?? 0,
          { height: 0, width: 0 },
          null,
          null,
          timestamp,
          false,
          false,
          props.textColor ? commandColorToColor(props.textColor) : Color.black(),
          [FontAttrsArray.fromString(`Name:${fontName}|Size:${fontSize}.000000|Range:{0, ${text.length}}`)],
          null,
          this.getAlignment(props.textAlignment),
          { fontSize, fontName },
          null,
          text,
          [FontAttrsArray.fromString(`Alignment:0|Color:000000|Name:${fontName}|Size:${fontSize}.000000|Range:{0, ${text.length}}`)]
        );
        break;
      }

      case 'shape': {
        const shapeTypeMap: Record<string, string> = {
          'rectangle': 'rectangle',
          'roundedRect': 'roundedRectangle',
          'circle': 'ellipse',
          'ellipse': 'ellipse',
          'triangle': 'isoscelesTriangle',
          'arrow': 'standardArrow',
          'diamond': 'octagon',
          'star': 'octagon',
          'hexagon': 'octagon',
        };

        const shapeType = shapeTypeMap[props.shapeType || 'rectangle'] || 'rectangle';
        const shapeMmo = ShapeFlowObject.default(shapeType);

        if (props.color) {
          shapeMmo.color = commandColorToColor(props.color);
        }

        if (props.frame) {
          shapeMmo.frame = {
            x: props.frame.x ?? shapeMmo.frame.x,
            y: props.frame.y ?? shapeMmo.frame.y,
            width: props.frame.width ?? shapeMmo.frame.width,
            height: props.frame.height ?? shapeMmo.frame.height,
          };
        }

        if (props.gradient && props.gradient.stops && props.gradient.stops.length >= 2) {
          shapeMmo.gradientDef = {
            type: props.gradient.type || 'linear',
            angle: props.gradient.angle ?? 180,
            stops: props.gradient.stops,
          };
        }

        mmo = shapeMmo;
        break;
      }

      default:
        return { success: false, error: `Cannot create MMO type: ${command.mmoType}` };
    }

    this.send({
      type: 'ADD_MMO',
      mmo,
      screenIndex,
    });

    return { success: true, mmoId: mmo.id, screenIndex };
  }

  /**
   * Execute delete command
   */
  private executeDelete(command: DeleteCommand, context: ExecutionContext): CommandResult {
    const screen = context.machineContext.flow.screens[context.screenIndex];
    if (!screen) {
      return { success: false, error: `Screen ${context.screenIndex} not found` };
    }

    const resolutionContext: ResolutionContext = {
      screen,
      screenIndex: context.screenIndex,
      contentMapping: context.contentMapping,
    };

    const resolved = resolveTarget(command.target, resolutionContext);
    if (!resolved.mmo) {
      return { success: false, error: resolved.reason };
    }

    this.send({
      type: 'DELETE',
      id: resolved.mmo.id,
      screenIndex: context.screenIndex,
    });

    return { success: true, mmoId: resolved.mmo.id, screenIndex: context.screenIndex };
  }

  /**
   * Execute reorder command
   */
  private executeReorder(command: ReorderCommand, context: ExecutionContext): CommandResult {
    const screen = context.machineContext.flow.screens[context.screenIndex];
    if (!screen) {
      return { success: false, error: `Screen ${context.screenIndex} not found` };
    }

    const resolutionContext: ResolutionContext = {
      screen,
      screenIndex: context.screenIndex,
      contentMapping: context.contentMapping,
    };

    const resolved = resolveTarget(command.target, resolutionContext);
    if (!resolved.mmo) {
      return { success: false, error: resolved.reason };
    }

    const mmoId = resolved.mmo.id;
    const mmoCount = screen.mixedMediaObjects?.length || 0;

    switch (command.direction) {
      case 'forward':
        this.send({
          type: 'REORDER_MMOS',
          direction: OrderShift.UP,
          id: mmoId,
        });
        break;

      case 'backward':
        this.send({
          type: 'REORDER_MMOS',
          direction: OrderShift.DOWN,
          id: mmoId,
        });
        break;

      case 'front':
        for (let i = 0; i < mmoCount; i++) {
          this.send({
            type: 'REORDER_MMOS',
            direction: OrderShift.UP,
            id: mmoId,
          });
        }
        break;

      case 'back':
        for (let i = 0; i < mmoCount; i++) {
          this.send({
            type: 'REORDER_MMOS',
            direction: OrderShift.DOWN,
            id: mmoId,
          });
        }
        break;
    }

    return { success: true, mmoId, screenIndex: context.screenIndex };
  }

  /**
   * Execute screen add command, creates a new blank screen
   */
  private executeScreenAdd(command: ScreenAddCommand, context: ExecutionContext): CommandResult {
    const screens = [...context.machineContext.flow.screens];
    const newScreen = Screens.default();
    const insertAt = command.insertAt ?? screens.length;
    screens.splice(insertAt, 0, newScreen);

    console.log(`[AI Executor] Adding new screen at index ${insertAt}`);
    this.send({
      type: 'REORDER_SCREENS',
      screens,
      newIndex: insertAt,
    });

    return { success: true, screenIndex: insertAt };
  }

  /**
   * Execute screen delete command, removes screen at target index
   */
  private executeScreenDelete(command: ScreenDeleteCommand, context: ExecutionContext): CommandResult {
    const screens = [...context.machineContext.flow.screens];
    const deleteIndex = command.target.screenIndex ?? context.screenIndex;

    if (screens.length <= 1) {
      return { success: false, error: 'Cannot delete the last remaining screen' };
    }
    if (deleteIndex < 0 || deleteIndex >= screens.length) {
      return { success: false, error: `Screen index ${deleteIndex} out of range` };
    }

    screens.splice(deleteIndex, 1);
    console.log(`[AI Executor] Deleting screen at index ${deleteIndex}`);
    this.send({
      type: 'REORDER_SCREENS',
      screens,
      kind: ReorderKind.Delete,
    });

    return { success: true, screenIndex: deleteIndex };
  }

  /**
   * Execute screen duplicate command, clones screen with new IDs
   */
  private executeScreenDuplicate(command: ScreenDuplicateCommand, context: ExecutionContext): CommandResult {
    const screens = [...context.machineContext.flow.screens];
    const sourceIndex = command.target.screenIndex ?? context.screenIndex;

    if (sourceIndex < 0 || sourceIndex >= screens.length) {
      return { success: false, error: `Screen index ${sourceIndex} out of range` };
    }

    const toBeCopied = screens[sourceIndex].clone();
    toBeCopied.id = guidGenerator();
    toBeCopied.mixedMediaObjects.forEach((mmo: FlowObjects) => {
      mmo.id = guidGenerator();
    });

    screens.push(toBeCopied);
    const newIndex = screens.length - 1;

    console.log(`[AI Executor] Duplicating screen ${sourceIndex} → new index ${newIndex}`);
    this.send({
      type: 'REORDER_SCREENS',
      screens,
      newIndex,
      kind: ReorderKind.Copy,
    });

    return { success: true, screenIndex: newIndex };
  }

  /**
   * Convert alignment string to enum
   */
  private getAlignment(align?: string): Alignment {
    switch (align?.toLowerCase()) {
      case 'center':
      case 'centre':
        return Alignment.CENTER;
      case 'right':
        return Alignment.RIGHT;
      default:
        return Alignment.LEFT;
    }
  }
}

/**
 * Create a command executor with the given send function
 */
export function createCommandExecutor(send: EventSender): CommandExecutor {
  return new CommandExecutor(send);
}
